import { Component, ErrorInfo, ReactNode } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import { Fonts, Palette } from '@/constants/theme';
import { requestTakeoverAndReload, scheduleTakeoverOnFocus } from '@/lib/tab-lock';

interface State {
  error: Error | null;
}

/**
 * Top-level React error boundary. Catches runtime errors thrown from any
 * descendant (including expo-sqlite worker callbacks that fire after a
 * stale OPFS handle conflict, which surface as "Unknown" without a stack
 * the user can act on) and renders a clear "Reload" UI instead of the
 * dev overlay.
 *
 * The dev overlay still shows on top in development — this just makes
 * sure production users see a calm screen instead of a black void.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log so the dev overlay still has the trace; production is silent.
    console.warn('AppErrorBoundary caught', error, info.componentStack);
    // OPFS lock conflicts that surface AFTER initial DB open (rare —
    // usually it's a stale handle from another tab waking up). Schedule
    // the takeover handshake — but ONLY when this tab is visible, so
    // backgrounded tabs that have yielded the lock don't fight to
    // reclaim it without the user's input.
    if (Platform.OS === 'web') {
      const msg = error.message || '';
      if (msg.includes('NoModificationAllowedError') || msg.includes('Access Handle')) {
        scheduleTakeoverOnFocus();
      }
    }
  }

  reset = () => {
    if (Platform.OS === 'web') {
      const msg = this.state.error?.message ?? '';
      if (msg.includes('NoModificationAllowedError') || msg.includes('Access Handle')) {
        // Coordinated takeover — older tab will yield, we reload after.
        void requestTakeoverAndReload();
        return;
      }
      // eslint-disable-next-line no-undef
      if (typeof window !== 'undefined') {
        window.location.reload();
        return;
      }
    }
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg = this.state.error.message || 'Unknown error';
    const isInsecureContext =
      msg.includes('navigator.storage') ||
      msg.includes('context is not secure') ||
      msg.includes('not supported by your browser');
    const isOpfsConflict =
      msg.includes('NoModificationAllowedError') ||
      msg.includes('Access Handle');
    const title = isInsecureContext
      ? "This browser can't store your data here"
      : isOpfsConflict
      ? 'Bringing Homestead to this tab…'
      : 'Something went wrong';
    const body = isInsecureContext
      ? "Homestead saves to your browser's secure storage. That only works on https:// or http://localhost. Open this URL on the same computer (localhost:8081) or use the Expo Go app to access it from your phone."
      : isOpfsConflict
      ? "Homestead is already open in another tab — we'll move it over here automatically. The other tab will gracefully refresh to a standby state. Tap below if it doesn't switch within a few seconds."
      : 'Reload the app — your data is safe on this device.';

    return (
      <View style={styles.shell}>
        <View style={styles.card}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.body}>{body}</Text>
          <Text style={styles.tech} numberOfLines={4}>{msg}</Text>
          <Pressable
            onPress={this.reset}
            style={({ pressed }) => [styles.btn, pressed && { opacity: 0.85 }]}>
            <Text style={styles.btnText}>{isOpfsConflict ? 'Move it here' : 'Reload'}</Text>
          </Pressable>
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  shell: {
    flex: 1, backgroundColor: Palette.sand,
    alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  card: {
    width: '100%', maxWidth: 380,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: 24, padding: 22,
  },
  title: {
    fontFamily: Fonts.serifBold, fontSize: 20, color: Palette.ink,
    letterSpacing: -0.3,
  },
  body: {
    marginTop: 8, fontFamily: Fonts.sansMedium, fontSize: 14,
    color: Palette.inkSoft, lineHeight: 21,
  },
  tech: {
    marginTop: 14, padding: 10, borderRadius: 10,
    backgroundColor: Palette.sand,
    fontFamily: Fonts.sansSemibold, fontSize: 12, color: Palette.inkFaint,
  },
  btn: {
    marginTop: 16, backgroundColor: Palette.forest,
    paddingVertical: 14, borderRadius: 14,
    alignItems: 'center',
  },
  btnText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 14.5 },
});
